import {dataTestId} from '@hconnect/uikit'
import {Check, Close} from '@mui/icons-material'
import {Button, DialogActions, DialogContent, DialogTitle} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {useStrengthMeasurementUnit} from '../hooks/useStrengthMeasurementUnit'
import {useStrengthTargetForm} from '../hooks/useStrengthTargetForm'
import {DayPeriod, StrengthTarget} from '../types'

import {StrengthTargetFormFields} from './StrengthTargetFormFields'

interface StrengthTargetFormContentProps {
  target?: StrengthTarget
  defaultDayPeriod: DayPeriod
  timezone: string
  onClose: () => void
  onSave: (target: StrengthTarget) => void
  existingTargets: StrengthTarget[]
  backendTargets: StrengthTarget[]
  isMobile: boolean
}

export const StrengthTargetFormContent: React.FC<StrengthTargetFormContentProps> = ({
  target,
  defaultDayPeriod,
  timezone,
  onClose,
  onSave,
  existingTargets,
  backendTargets,
  isMobile
}) => {
  const {t} = useTranslation()

  const {
    form,
    isEdit,
    createAnother,
    showAtLeastOneValueError,
    hasAnyErrors,
    setCreateAnother,
    onSubmit
  } = useStrengthTargetForm({
    target,
    defaultDayPeriod,
    timezone,
    existingTargets,
    backendTargets,
    onSave,
    onClose
  })

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {errors}
  } = form

  const dayPeriodValue = watch('dayPeriod')

  const unit = useStrengthMeasurementUnit(dayPeriodValue)

  const handleCreateAnotherChange = (checked: boolean) => {
    setCreateAnother(checked)
    setValue('createAnother', checked)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-test-id="strength_target_form">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2
        }}
        {...dataTestId('dialog_title')}
      >
        {isEdit
          ? t('materialsSettings.strengthTargets.editTarget')
          : t('materialsSettings.strengthTargets.newTarget')}
        <Close
          onClick={onClose}
          sx={{
            cursor: 'pointer',
            color: (theme) => theme.palette.text.secondary,
            '&:hover': {
              color: (theme) => theme.palette.text.primary
            }
          }}
          {...dataTestId('close_icon')}
        />
      </DialogTitle>

      <DialogContent sx={{pt: 0}} {...dataTestId('dialog_content')}>
        <StrengthTargetFormFields
          control={control}
          errors={errors}
          isEdit={isEdit}
          unit={unit}
          createAnother={createAnother}
          showAtLeastOneValueError={showAtLeastOneValueError}
          timezone={timezone}
          onCreateAnotherChange={handleCreateAnotherChange}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: {xs: 2, md: 3},
          pb: {xs: 2, md: 3},
          pt: 2,
          flexDirection: {xs: 'column-reverse', md: 'row'},
          gap: {xs: 1, md: 0}
        }}
        {...dataTestId('dialog_actions')}
      >
        <Button
          onClick={onClose}
          startIcon={<Close />}
          fullWidth={isMobile}
          sx={{textTransform: 'none'}}
          {...dataTestId('cancel_button')}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<Check />}
          fullWidth={isMobile}
          sx={{textTransform: 'none'}}
          disabled={hasAnyErrors}
          {...dataTestId('save_button')}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </form>
  )
}
